<template>
<div class="container">
	<div class="page">
		<!-- Header -->
		<div class="page-header">
			<div class="row">
				<div class="col-md-9">
					<h1 class="page-title">{{ $t('Request Product') }}</h1>
				</div>
			</div>
		</div>

		<!-- Table Menu -->
		<div class="table-list-menu d-lg-flex align-items-center">
			<div class="table-actions d-flex align-items-center flex-wrap">
				<!-- Add Product Request -->
				<a href="/request-product" class="btn btn-primary btn-sm flex-center mr-2">
					<i class="material-icons">add</i>
					<span>{{ $t('Request Product') }}</span>
				</a>
			</div>

			<div class="table-filters text-right d-flex align-items-center ml-a">
				<!-- Clear Filter -->
				<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
					{{ $t('Clear Filter') }}
				</button>
				<!-- Date Filter -->
				<daterangepicker :presets="filterDatePresets" :start="since" :end="until" @selected="setFilterDate">
				</daterangepicker>
			</div>
		</div>

		<!-- Main Table -->
		<div class="products-list table-responsive" v-if="isRouteChecked">
			<table class="products-list-table table table-primary table-striped mb-0">
				<thead>
					<tr>
						<th width="5%">No.</th>
						<th class="text-left">
							{{ $t('Jenis Produk') }}
						</th>
						<th class="text-left">
							{{ $t('Foto Produk') }}
						</th>
						<th class="text-left">
							{{ $t('Link Produk') }}
						</th>
						<th class="text-left">
							{{ $t('Deskripsi Produk') }}
						</th>
						<th class="text-left">
							{{ $t('Jumlah Penjualan') }}
						</th>
						<th class="text-left">
							{{ $t('Tanggal') }}
						</th>
						<th class="text-left">
							{{ $t('Status') }}
						</th>
						<th class="text-center">
							{{ $t('Action') }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, idx) in productRequests" :key="item._id.$oid">
						<!-- No. -->
						<td class="text-center">{{ idx + 1 }}.</td>
						<!-- Category -->
						<td class="mw-150">
							<div>{{ formatCategory(item.category) }}</div>
						</td>
						<!-- Images -->
						<td>
							<div class="row mw-250">
								<div class="col px-1"
									v-for="(image, index) in item.product_images.slice(0, maxImageShown + 1)"
									:key="index + 0">
									<div v-if="image === '' || (index > maxImageShown - 1)" class="pc-image-empty square-img">
										<div class="image-upload-placeholder placeholder-height" :class="{ 'cursor-pointer': index >= maxImageShown }">
											<i class="fi fi-rr-picture image-placeholder fs-16"
												v-if="index < maxImageShown"></i>
											<div class="text-placeholder pt-0 fs-12">
												<span v-if="index < maxImageShown">
													Foto {{ index + 1 }}
												</span>
												<span v-else @click="editProductRequest(item)">
													2+
												</span>
											</div>
										</div>
									</div>
									<div v-else class="pc-image square-img cursor-pointer">
										<div class="img" :style="{ backgroundImage: `url('${image}')` }" @click="enlargeImage(image)">
										</div>
									</div>
								</div>
							</div>
						</td>
						<!-- Reference Link -->
						<td>
							<div>
								<a :href="item.reference_link" target="_blank" v-if="item.reference_link">{{ item.reference_link }}</a>
								<span v-else>-</span>
							</div>
						</td>
						<!-- Description -->
						<td class="mw-150">
							<div>{{ formatDescription(item) }}</div>
						</td>
						<!-- Sales -->
						<td class="mw-150">
							<div>{{ formatSales(item) }}</div>
						</td>
						<!-- Date -->
						<td class="mw-150">
							<div>{{ formatDate(item.created_at) }}</div>
						</td>
						<!-- Status -->
						<td>
							<div :class="`text-${formatStatus(item.status).color}`">{{
								formatStatus(item.status).label
							}}</div>
						</td>
						<!-- Action -->
						<td class="text-center">
							<i class="fas fa-edit cursor-pointer" @click="editProductRequest(item)"></i>
						</td>
					</tr>
				</tbody>
			</table>
			<div class="list-empty table-empty" v-if="productRequests.length === 0 && !isFetching">
				<i class="material-icons mr-2 va-m">error_outline</i>
				<span class="va-m" v-if="keyword === ''">{{ $t("You haven't created any product request yet") }}.</span>
				<span class="va-m" v-else>{{ $t('No product request was found') }}</span>
			</div>
		</div>

		<!-- Table Loader -->
		<div class="table-fetch-status" v-if="isFetching">
			<div class="is-loading large dark m-auto"></div>
		</div>
	</div>

	<!-- Modals -->
	<div class="modals">

		<!-- Modal Edit -->
		<modal class="table-filter-modal" size="medium" @close="isShowEdit = false" v-if="isShowEdit">
			<div slot="header">
				<h3>Edit Request Product</h3>
			</div>
			<div slot="body">

				<!-- Categories -->
				<div class="col-md-12">
					<div class="form-group">
						<label>{{ $t('Jenis Produk Yang Diminati') }} <span class="text-danger">*</span></label>
						<div class="mb-2">
							<multiselect placeholder="Select Category" label="name" track-by="slug"
								@close="categoryValidation" @select="onCategoryChange" v-model="selectedCategory"
								:options="categories" :show-labels="false" :allow-empty="false"
								:disabled="categories.length === 0">
							</multiselect>
							<small v-if="errorMessages.category" class="text-danger">{{ errorMessages.category
							}}</small>
						</div>

						<div>
							<multiselect v-if="subCategories" placeholder="Select Sub Category" label="name" track-by="slug"
								v-model="selectedSubCategory" :options="subCategories"
								:show-labels="false" :allow-empty="false" :disabled="subCategories.length === 0">
							</multiselect>
							<small v-if="errorMessages.subCategory" class="text-danger">{{ errorMessages.subCategory
							}}</small>
						</div>
					</div>
				</div>

				<div class="col-md-12">
					<div class="form-group">
						<label>{{ $t('Harga Rekomendasi') }} <span class="text-danger">*</span></label>
						<div class="input-group">
							<div class="input-group-prepend">
								<div class="input-group-text">
									{{ currencySymbol }}
								</div>
							</div>
							<input type="text" class="input form-control" min="0"
								:class="{ 'is-invalid': errorMessages.price }" @input="onPriceChange"
								@change="onPriceChanges" v-model="price" />
							<small v-if="errorMessages.price" class="invalid-feedback">{{
								errorMessages.price }}</small>
						</div>
					</div>
				</div>

				<div class="col-md-12">
					<div class="form-group">
						<label>{{ $t('Deskripsi Produk') }} ({{ $t('Spesifikasi Produk') }}) <span
							class="text-danger">*</span></label>
						<textarea class="form-control" :class="{ 'is-invalid': errorMessages.description }"
							placeholder="Masukkan Deskripsi Produk..." rows="3" required
							v-model="description"></textarea>
						<small v-if="errorMessages.description" class="invalid-feedback">{{ errorMessages.description
						}}</small>
					</div>
				</div>

				<div class="row mx-0 w-100">
					<div class="col-md-12">
						<label>{{ $t('Contoh Foto Produk') }}</label>
						<p class="pc-p">
							Format gambar .jpg .jpeg .png dan ukuran minimum 300 x 300px (Untuk gambar
							optimal gunakan ukuran minimum 700 x 700 px). Ukuran maksimum gambar adalah <b>2MB</b>.
						</p>
						<small v-if="errorMessages.image" class="text-danger">{{ errorMessages.image }}</small>
					</div>
					<div class="col-md-12">
						<div class="box__content">
							<div class="pc-images-list">
								<draggable draggable=".draggable" v-model="product.images" v-bind="dragOptions"
									@start="drag = true" @end="drag = false">
									<transition-group class="row small-gutter" type="transition"
										:name="!drag ? 'flip-list' : null">
										<div class="col" :class="{ draggable: image !== '' }"
											v-for="(image, index) in product.images" :key="index + 0">
											<div v-if="isLoading && image === ''" class="pc-image-empty">
												<div class="image-loading">
													<div class="is-loading"></div>
												</div>
											</div>
											<div v-else-if="image === ''" class="pc-image-empty"
												:class="{ 'cursor-pointer': isStatusReview }"
												@click="uploadImages(index)">
												<div class="image-upload-placeholder">
													<i class="fi fi-rr-picture image-placeholder"></i>
													<div class="text-placeholder">
														Foto {{ index + 1 }}
													</div>
												</div>
											</div>
											<div v-else class="pc-image" :class="{
												'is-loading': image === '',
											}">
												<div class="img" :style="{ backgroundImage: `url('${image}')` }" @click="enlargeImage(image)">
												</div>
												<i class="material-icons" v-tooltip="'Remove'"
													v-if="isStatusReview"
													@click="removeProductImages(index)">close</i>
											</div>
										</div>
									</transition-group>
								</draggable>
							</div>
							<div class="form-group buttons m-0 hidden">
								<input type="file" accept="image/*" class="input-file" ref="imageUploader"
									id="pc-product-images-file" @change="onProductImagesChange" />
							</div>
						</div>
					</div>
				</div>

				<div class="col-md-12">
					<div class="form-group">
						<label>{{ $t('Link Referensi Produk') }}</label>
						<input type="text" class="form-control" placeholder="https://" v-model.trim="referenceLink">
						<small v-if="errorMessages.referenceLink" class="text-danger">{{ errorMessages.referenceLink
						}}</small>
					</div>
				</div>

				<div class="col-md-12">
					<div class="form-group">
						<label>{{ $t('Berapa rata-rata jumlah penjualan Anda?') }} <span
								class="text-danger">*</span></label>
						<div class="input-group">
							<div class="input-group-prepend">
								<button
									class="btn btn-outline-secondary dropdown-toggle"
									:class="{
										'border-error': errorMessages.selectedSalesPeriod,
										'border-normal': !errorMessages.selectedSalesPeriod,
									}"
									type="button"
									data-toggle="dropdown"
									aria-expanded="false"
								>
									{{ selectedSalesPeriodLabel }}
								</button>
								<div class="dropdown-menu">
									<a
										class="dropdown-item"
										@click="assignPeriod(period.value)"
										v-for="(period, idx) in salesPeriodOptions"
										:key="idx"
									>
										{{ period.label }}
									</a>
								</div>
							</div>
							<input
								type="text"
								class="input form-control"
								min="0"
								:class="{ 'is-invalid': errorMessages.salesValue }"
								@input="onSalesValueChange"
								@change="onSalesValueChanges"
								v-model="salesValue"
							/>
						</div>
						<div class="row">
							<small v-if="errorMessages.selectedSalesPeriod" class="col-md-12 text-danger">{{
								errorMessages.selectedSalesPeriod }}</small>
							<small v-if="errorMessages.salesValue" class="col-md-12 text-danger">{{
								errorMessages.salesValue }}</small>
						</div>
					</div>
				</div>
			</div>
			<div slot="footer">
				<button type="button" class="btn btn-light" @click="isShowEdit = false">
					{{ $t('Close') }}
				</button>
				<button type="button" class="btn btn-primary"
					:class="{ 'is-loading': isUpdateProductRequest }"
					:disabled="!isStatusReview"
					v-tooltip="disabledTooltip"
					@click="updateProductRequest">
					Update
				</button>
			</div>
		</modal>

	</div>

	<notifications group="products" position="bottom right" />
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import Modal from '@/components/Modal.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import { updateProductRequest } from '@/api/product-request';
import homepageApi from '@/api/homepage';
import mediaApi from '@/api/media';
import { statusOptions, salesRangeOptions, salesPeriodOptions } from '@/lib/statics';
import {
	getDateRange,
	duplicateVar,
	getAxiosErrorMessage,
	isUrlValid,
} from '@/lib/helper';

const defaultFilterDate = getDateRange();

export default {
	name: 'ProductRequestList',
	components: {
		Modal,
		Daterangepicker,
		draggable,
	},
	data() {
		return {
			keyword: '',
			limit: 25,
			sort_by: 'created_at',
			sort: 'desc',
			filterDate: defaultFilterDate,
			isUpdateProductRequest: false,
			statusOptions,
			selectedProductRequest: null,
			isShowEdit: false,
			isFetchingCategories: false,
			categories: [],
			subCategories: [],
			selectedCategory: null,
			selectedSubCategory: null,
			description: '',
			price: 0,
			isLoading: false, // is uploading images
			drag: false,
			dragOptions: {
				animation: 100,
			},
			product: {
				images: ['', '', '', '', ''],
			},
			productImages: [],
			productImagesLimit: 5,
			referenceLink: '',
			salesRangeOptions,
			salesPeriodOptions,
			selectedSalesPeriod: '',
			selectedSales: '',
			salesValue: 0,
			errorMessages: {
				category: '',
				subCategory: '',
				description: '',
				price: '',
				image: '',
				referenceLink: '',
				selectedSales: '',
				salesValue: '',
				selectedSalesPeriod: '',
			},
			maxImageShown: 3,
		};
	},
	methods: {
		...mapActions({
			getProductRequests: 'productRequest/getProductRequests',
			resetProductRequests: 'productRequest/resetProductRequests',
		}),
		getParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			return params;
		},
		fetchProductRequests(reset = true) {
			if (reset) {
				this.resetProductRequests();
			}
			const params = this.getParams();
			this.getProductRequests(params);
		},
		checkCapabilities() {
			this.isRouteChecked = true;
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchProductRequests();
		},
		clearFilter() {
			window.location.href = '/request-product/list';
		},
		formatDescription(item) {
			return item.description || '-';
		},
		formatSales(item) {
			let salesLabel = '-';

			const sales = this.salesRangeOptions.find(rev => rev.value === item.sales);
			if (sales && sales.label) {
				salesLabel = sales.label;
			}

			if (salesLabel === '-' && item.sales) {
				salesLabel = item.sales;
			}

			const salesPeriod = item.sales_period ? `/ ${item.sales_period}` : '/ day';
			salesLabel = `${salesLabel} ${salesPeriod}`;

			return salesLabel;
		},
		formatCategory(category) {
			let categoryString = category.name;
			categoryString += category.sub_category ? ` - ${category.sub_category.name}` : '';
			return categoryString;
		},
		formatDate(dateString) {
			const dateNumberLong = date => new Date(parseInt(date.$date.$numberLong));
			const date = typeof dateString === 'object' ? dateNumberLong(dateString) : dateString;
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			const formattedDate = this.moment
				.utc(date)
				.tz(timezone)
				.format('DD MMM YYYY');
			return dateString && dateString !== '' ? formattedDate : '-';
		},
		formatStatus(statusValue) {
			const selectedStatus = this.statusOptions.find(status => status.value === statusValue);
			return {
				label: selectedStatus?.label,
				color: selectedStatus?.color,
			};
		},
		categoryValidation() {
			if (this.selectedCategory
				&& this.selectedCategory.slug !== 'uncategorized'
				&& this.selectedCategory.slug !== 'produk-lainnya') {
				this.errorMessages.category = '';
			} else {
				this.errorMessages.category = 'Kategori Wajib Dipilih';
			}
		},
		subCategoryValidation() {
			if (this.selectedSubCategory) {
				this.errorMessages.subCategory = '';
			} else {
				this.errorMessages.subCategory = 'Sub Kategori Wajib Dipilih';
			}
		},
		descriptionValidation() {
			if (this.description.length <= 0) {
				this.errorMessages.description = 'Deskripsi Wajib Diisi';
			} else {
				this.errorMessages.description = '';
			}
		},
		priceValidation() {
			if (parseInt(this.price) === 0 || this.price.length <= 0) {
				this.errorMessages.price = 'Harga Wajib Diisi';
			} else {
				this.errorMessages.price = '';
			}
		},
		fetchCategories() {
			this.isFetchingCategories = true;
			const callback = (response) => {
				this.isFetchingCategories = false;
				const categories = response.data;
				this.categories = categories;
			};
			const errorCallback = () => {
				this.isFetchingCategories = false;
			};
			homepageApi.getAllCategories(callback, errorCallback);
		},
		onCategoryChange(category) {
			this.selectedSubCategory = null;
			this.subCategories = category?.sub_categories;
		},
		parsePricing(price) {
			let parsedPrice = price.replace(/[^0-9]/g, '').trim();
			const parsedPriceLength = parsedPrice.length;
			if (parsedPriceLength > 1 && parsedPrice.charAt(0) === '0') {
				parsedPrice = parsedPrice.substring(1, parsedPriceLength);
			}
			return parsedPrice;
		},
		onPriceChange() {
			this.price = this.parsePricing(this.price);
		},
		onPriceChanges() {
			if (this.price === '') {
				this.price = 0;
			}
		},
		onSalesValueChange() {
			this.salesValue = this.parsePricing(this.salesValue);
		},
		onSalesValueChanges() {
			if (this.salesValue === '') {
				this.salesValue = 0;
			}
		},
		uploadImages(index) {
			if (!this.isStatusReview) {
				return;
			}
			this.currentIndexUploadImage = index;
			const inputFile = this.$refs.imageUploader;
			inputFile.click();
		},
		onProductImagesChange(event) {
			this.isLoading = true;
			const maxUploadSize = 2; // MB
			const input = event.target;
			for (let i = 0; i < input.files.length; i++) {
				const image = input.files[i];

				// Check File Size
				let fileSize = image.size;
				fileSize /= 1048576;

				// Limit Images
				const isLimitReached =
					this.productImages.length === this.productImagesLimit;
				const isFileSizeTooLarge = fileSize > maxUploadSize;

				// Show Notice if size too large
				if (isFileSizeTooLarge) {
					const noticeMessage = `'${image.name}' size is too large (max 2MB)`;
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				if (isLimitReached) {
					const noticeMessage = 'Maksimum upload 5 foto produk';
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				this.productImages.push(image);

				const previewIndex = this.currentIndexUploadImage;

				const productImages = duplicateVar(this.product.images);
				this.$set(productImages, previewIndex, '');
				this.product.images = productImages;

				// Load the image file into a canvas element
				const img = new Image();
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				const reader = new FileReader();
				// eslint-disable-next-line no-loop-func
				reader.onload = () => {
					img.onload = () => {
						const size = Math.max(img.width, img.height);
						canvas.width = size;
						canvas.height = size;
						const offsetX = (size - img.width) / 2;
						const offsetY = (size - img.height) / 2;
						ctx.fillStyle = '#fff';
						ctx.fillRect(0, 0, size, size); // fill the canvas with white
						ctx.drawImage(img, offsetX, offsetY);

						// Convert the canvas to a blob and upload it to the server
						canvas.toBlob((blob) => {
							// Start Upload
							const uploadParams = new FormData();
							uploadParams.append('image', blob);

							const callback = (response) => {
								this.isLoading = false;
								const path = response.data;
								const url = `${this.mediaUrlPrefix}/${path}`;
								this.$set(this.product.images, previewIndex, url);
								this.linkImageValidation();
							};

							const errorCallback = () => {
								this.isLoading = false;
								// eslint-disable-next-line
								this.productImages.splice(previewIndex, 1);
								this.product.images.splice(previewIndex, 1);
								this.linkImageValidation();
							};

							// Upload Image
							mediaApi.uploadImagePublic(uploadParams, callback, errorCallback);
						}, image.type, 0.9);
					};
					img.src = reader.result;
				};
				reader.readAsDataURL(image);
			}
			event.target.files = null;
			event.target.value = '';
		},
		removeProductImages(index) {
			this.productImages.splice(index, 1);
			this.product.images.splice(index, 1, '');
		},
		linkImageValidation() {
			const image = this.product.images.find(img => img !== '');
			if (!image && !this.referenceLink) {
				this.errorMessages.image = 'Upload minimal 1 gambar atau isi Link Referensi';
				this.errorMessages.referenceLink = 'Isi Link Referensi atau Upload minimal 1 gambar';
			} else if (this.referenceLink && !isUrlValid(this.referenceLink)) {
				this.errorMessages.referenceLink = 'Link harus valid dan diawali https:// atau http://';
			} else {
				this.errorMessages.image = '';
				this.errorMessages.referenceLink = '';
			}
		},
		salesValidation() {
			if (this.selectedSales === '') {
				this.errorMessages.selectedSales = 'Total Penjualan Per Hari Wajib Dipilih';
			} else {
				this.errorMessages.selectedSales = '';
			}
		},
		salesValueValidation() {
			if (parseInt(this.salesValue) === 0 || this.salesValue.length <= 0) {
				this.errorMessages.salesValue = 'Rata-rata Jumlah Penjualan Wajib Diisi';
			} else {
				this.errorMessages.salesValue = '';
			}
		},
		salesPeriodValidation() {
			this.errorMessages.selectedSalesPeriod = '';

			if (this.selectedSalesPeriod === '') {
				this.errorMessages.selectedSalesPeriod = 'Periode Wajib Dipilih';
			}
		},
		validateAllForm() {
			this.categoryValidation();
			// this.subCategoryValidation();
			this.descriptionValidation();
			this.priceValidation();
			this.linkImageValidation();
			// this.salesValidation();
			this.salesValueValidation();
			this.salesPeriodValidation();
		},
		assignCategory(selectedProductRequest) {
			this.selectedCategory = selectedProductRequest.category;
			const categories = this.categories.find(category => category.slug === this.selectedCategory.slug);
			this.onCategoryChange(categories);
			const category = selectedProductRequest.category;
			const subCategory = category.sub_category ? category.sub_category : null;
			this.selectedSubCategory = subCategory;
		},
		editProductRequest(item) {
			this.selectedProductRequest = item;
			this.assignCategory(this.selectedProductRequest);
			this.description = this.selectedProductRequest.description || '';
			this.price = this.selectedProductRequest.price || 0;
			this.product.images = this.selectedProductRequest.product_images;
			this.referenceLink = this.selectedProductRequest.reference_link;
			this.selectedSales = this.selectedProductRequest.sales;
			this.salesValue = this.selectedSales;
			this.selectedSalesPeriod = this.selectedProductRequest?.sales_period ?? '';
			this.isShowEdit = true;
		},
		getCategoryParams() {
			const selectedSubCategory = duplicateVar(this.selectedSubCategory);
			const selectedCategory = duplicateVar(this.selectedCategory);
			delete selectedSubCategory.sub_sub_categories;
			delete selectedCategory.sub_categories;
			delete selectedCategory._id;
			delete selectedSubCategory._id;
			delete selectedSubCategory.parent_id;

			const category = {
				...selectedCategory,
				sub_category: selectedSubCategory,
			};

			return category;
		},
		updateProductRequest() {
			this.validateAllForm();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isUpdateProductRequest = true;

			const category = this.getCategoryParams();

			const params = {
				...this.selectedProductRequest,
				category,
				description: this.description,
				price: parseInt(this.price),
				product_images: this.product.images,
				reference_link: this.referenceLink,
				sales: this.salesValue,
				sales_period: this.selectedSalesPeriod,
			};

			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'app',
					title: 'Update Product Request',
					text: message,
					type: 'success',
				});
				this.isUpdateProductRequest = false;
				this.isShowEdit = false;
				this.fetchProductRequests();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Update Product Request',
					text: message,
					type: 'error',
				});
				this.isUpdateProductRequest = false;
			};

			updateProductRequest(this.selectedProductRequest._id.$oid, params, callback, errorCallback);
		},
		checkUser() {
			if (!this.isDropshipper) {
				this.$router.push('/403');
			}
		},
		assignPeriod(value) {
			this.selectedSalesPeriod = value;
		},
		enlargeImage(src) {
			const overlay = document.createElement('div');
			overlay.style.cssText = `
				background: rgba(0, 0, 0, 0.5) url(${src}) no-repeat center;
				background-size: contain;
				width: 100%;
				height: 100%;
				position: fixed;
				top: 0;
				left: 0;
				z-index: 10000;
				cursor: zoom-out;
			`;
			overlay.addEventListener('click', () => {
				document.body.removeChild(overlay);
			});
			document.body.appendChild(overlay);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			productRequests: 'productRequest/allProductRequests',
			isFetching: 'productRequest/isFetching',
			isAll: 'productRequest/isAll',
			page: 'productRequest/page',
			isFetched: 'productRequest/isFetched',
			filterDatePresets: 'filterDatePresets',
			currency: 'currency',
			isDropshipper: 'isDropshipper',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		isFilterOn() {
			return (
				this.since
				|| this.until
			);
		},
		currencySymbol() {
			return this.getCurrencySymbol(this.currency);
		},
		isStatusReview() {
			const isReview = this.selectedProductRequest?.status === 'review';
			return isReview;
		},
		disabledTooltip() {
			const tooltip = !this.isStatusReview ? this.$t('Data can only be edited when in \'review\' status') : '';
			return tooltip;
		},
		selectedSalesPeriodLabel() {
			let label = 'Periode';
			const period = this.salesPeriodOptions.find(period => period.value === this.selectedSalesPeriod);

			if (period && period.label) {
				label = period.label;
			}

			return label;
		},
	},
	watch: {
		user() {
			this.initFilterDate();
			this.checkCapabilities();
		},
		salesValue() {
			this.salesValueValidation();
		},
		selectedSalesPeriod() {
			this.salesPeriodValidation();
		},
		'$route.query': {
			immediate: true,
			handler() {
				this.fetchProductRequests();
			},
		},
	},
	created() {
		this.checkUser();
		if (this.user) {
			this.initFilterDate();
			this.checkCapabilities();
		}
		// Fetch Product Requests
		this.fetchProductRequests();
		// Fetch Categories
		this.fetchCategories();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.square-img {
	max-width: 60px;
	max-height: 60px;
}

.mw-150 {
	min-width: 150px;
}

.mw-250 {
	min-width: 250px;
}

.fs-16 {
	font-size: 16px !important;
}

.fs-12 {
	font-size: 12px !important;
}

.placeholder-height {
	height: 60px;
}

.border-normal {
	border: 1px solid #ced4da;
}

.border-error {
	border: 1px solid red;
}
</style>
